/**
 * @file CustomShippingSection.tsx
 *
 * The "Custom shipping" section of the Shipping overview. Reads the zones (with
 * their methods and locations) and the region tree from the shipping store,
 * builds the display rows, and wires drag-reorder (optimistic), edit (routed),
 * and delete (confirmed, via {@link useDeleteConfirm}) onto the shared
 * {@link OptionsListManager} card / {@link DraggableDataTable}.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError } from '../feedback/toast';
import { useShippingStore } from '../stores/shipping.store';
import { useDeleteConfirm } from '../hooks/use-delete-confirm';
import { ConfirmModal } from '../components/ConfirmModal';
import { OptionsListManager } from './OptionsListManager';
import { useLocalizedContinents } from './use-localized-continents';
import { buildZoneRows, type ZoneRow } from './shipping-list';

/**
 * Renders the custom-shipping-zones section.
 *
 * @returns The section card.
 */
export function CustomShippingSection() {
  const intl = useIntl();
  const navigate = useNavigate();

  const zones = useShippingStore((s) => s.zones);
  const zoneMethods = useShippingStore((s) => s.zoneMethods);
  const zoneLocations = useShippingStore((s) => s.zoneLocations);
  const continents = useLocalizedContinents();
  const reorderZones = useShippingStore((s) => s.reorderZones);
  const deleteZone = useShippingStore((s) => s.deleteZone);

  const del = useDeleteConfirm<ZoneRow>(
    (row) => deleteZone(row.zoneId),
    intl.formatMessage(adminMessages.shippingDeleteZoneError),
    {
      success: intl.formatMessage(adminMessages.toastDeleted),
      error: intl.formatMessage(adminMessages.toastError),
    },
  );

  const rows = useMemo(
    () =>
      buildZoneRows(zones, zoneMethods, zoneLocations, continents, {
        flatRate: intl.formatMessage(adminMessages.shippingMethodFlatRate),
        freeShipping: intl.formatMessage(adminMessages.shippingMethodFreeShipping),
        allRegions: intl.formatMessage(adminMessages.shippingEverywhere),
        noMethods: intl.formatMessage(adminMessages.shippingNoMethods),
      }),
    [zones, zoneMethods, zoneLocations, continents, intl],
  );

  const handleReorder = (next: ZoneRow[]): void => {
    void reorderZones(next.map((row) => row.zoneId)).then((ok) => {
      if (!ok) {
        notifyError(intl.formatMessage(adminMessages.toastError));
      }
    });
  };

  return (
    <>
      <OptionsListManager<ZoneRow>
        testId="shipping-zones"
        tileName="shipping-v3"
        title={intl.formatMessage(adminMessages.shippingCustomTitle)}
        description={intl.formatMessage(adminMessages.shippingCustomDescription)}
        addButtonLabel={intl.formatMessage(adminMessages.shippingAddOption)}
        dragHandleAriaLabel={intl.formatMessage(adminMessages.shippingDragToReorder)}
        headerColumns={[
          {
            name: {
              label: intl.formatMessage(adminMessages.shippingFieldName),
              className: 'gv-col-truncate',
            },
          },
          {
            methodTypeLabel: {
              label: intl.formatMessage(adminMessages.shippingFieldType),
              className: 'gv-col-truncate',
            },
          },
          {
            shipsToLabel: {
              label: intl.formatMessage(adminMessages.shippingFieldShipsTo),
              className: 'gv-col-truncate',
            },
          },
        ]}
        rows={rows}
        actions={[
          {
            icon: 'edit',
            label: intl.formatMessage(adminMessages.shippingEdit),
            testId: 'shipping-zones-edit',
            onAction: (row) => navigate(`/shipping/custom-shipping/${row.zoneId}/edit`),
          },
          {
            icon: 'delete',
            label: intl.formatMessage(adminMessages.shippingDelete),
            testId: 'shipping-zones-delete',
            onAction: (row) => del.ask(row),
          },
        ]}
        onAdd={() => navigate('/shipping/custom-shipping/new')}
        onReorder={handleReorder}
        getPreviewLabel={(row) => row.name}
      />

      {del.target && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.shippingDeleteZoneTitle)}
          message={intl.formatMessage(adminMessages.shippingDeleteZoneMessage, {
            name: del.target.name,
          })}
          confirmLabel={intl.formatMessage(adminMessages.shippingDelete)}
          cancelLabel={intl.formatMessage(adminMessages.shippingCancel)}
          loadingLabel={intl.formatMessage(adminMessages.shippingDeleteLoading)}
          confirmVariant="destructive"
          loading={del.deleting}
          errorMessage={del.error ?? undefined}
          onPrimaryClick={del.confirm}
          onSecondaryClick={del.cancel}
          onClose={del.cancel}
        />
      )}
    </>
  );
}
